/**
 * WHAT A ROLE BOUNCE THREW AWAY, KEPT — and thrown away again when the session ends.
 *
 * The role-denial branch in `./guards.ts` used to log a warn nobody reads and
 * redirect. Everything that made the denial explicable died there: WHY the door
 * closed, WHO can open it, and — the one the person actually needs — the URL
 * they were trying to reach, which `homeRouteFor()` discards along with its
 * query string. Someone following a report link to
 * `/atlas/carriers/80321?tab=crashes` landed on a dashboard with no message and
 * no way back to the link.
 *
 * WHY THIS IS A CARRIER AND NOT A VIEW (F2). The one rendered `DenialView` is
 * ops-local — `packages/ops/src/components/atlas/kit/DenialView.vue` — and the
 * guard module is imported by five apps, so importing it there would drag the
 * ops kit into the portal, apply and broker bundles. The shared half is the
 * DATA; each app renders it with what it has, and an app that renders nothing
 * behaves exactly as it does today.
 *
 * WHY NOT THE QUERY STRING. Putting the original URL on the bounce target
 * (`/?denied_from=/atlas/carriers/80321`) would survive a reload, and would
 * also write the denied path into browser history, the referer header and any
 * analytics that sees the URL. A module-scoped ref is read by the same document
 * that was bounced and by nothing else.
 *
 * WHY THIS IS ITS OWN MODULE AND NOT PART OF `./guards.ts` (AST3-1, 2026-09-22).
 * The carrier has to be cleared by the auth store's `clearAuth()`, which is the
 * ONE teardown both logout and a failed session restore go through. `guards.ts`
 * imports `../stores/auth`, so the store cannot import the guard back without a
 * cycle. This module imports `vue` and nothing else, so both sides can hold it.
 *
 * LIFETIME — the bug this file exists to close. Until AST3-1 the ref outlived
 * the session that produced it: a staff denial of
 * `/admin/users?client=ACME#a-driver` survived a REAL `logout()` and was
 * readable by whoever signed in next on that tab, including a `developer`
 * account. `originalUrl` is unminimised arbitrary query and hash, so that is a
 * client/driver identifier handed across an identity boundary. The ref is now
 * scoped to the session: set at the moment of the bounce, replaced by the next
 * denial, dropped by `clearRouteDenial()` — which `clearAuth()` calls on every
 * teardown — and dropped again by `beginAuthSession()` when a new identity is
 * established, so it cannot be inherited in either direction.
 *
 * It still does NOT self-clear on the next successful navigation: the bounce's
 * own hop to the home route is a successful navigation, and clearing there
 * would destroy the denial before the view that renders it has mounted.
 */
import { shallowRef, type ShallowRef } from 'vue'

export interface RouteDenial {
  /** Why the door closed, in the denied person's words. */
  reason: string
  /** Who can open it — `meta.grantedBy`, else `DEFAULT_GRANTED_BY`. */
  grantedBy: string
  /** The full URL the bounce dropped, query and hash included. */
  originalUrl: string
  /** That URL's path alone, for a caller that wants to log or retry without the query. */
  deniedPath: string
  /** The roles the route declared. */
  requiredRoles: readonly string[]
  /** The role the account actually carried, or undefined when it carried none. */
  userRole?: string
  /** `Date.now()` at the bounce, so a stale denial is recognisable as stale. */
  at: number
}

/** Used when a route names nobody. Reads as "Ask a ClaimShield administrator for access." */
export const DEFAULT_GRANTED_BY = 'a ClaimShield administrator'

const denialRef = shallowRef<RouteDenial | null>(null)

/**
 * The live denial, for a view that renders reactively. Readonly by type: the
 * guard is the only writer, so an app cannot manufacture a denial that never
 * happened.
 */
export const routeDenial: Readonly<ShallowRef<RouteDenial | null>> = denialRef

/**
 * Record a denial. The guard is the only caller — the writer is not exported
 * from the shared barrel for the reason in `routeDenial`'s docblock.
 */
export function recordRouteDenial(denial: RouteDenial): void {
  denialRef.value = denial
}

/** Read the current denial without clearing it. */
export function peekRouteDenial(): RouteDenial | null {
  return denialRef.value
}

/** Read the current denial and clear it — the read-once path for a view that mounts after the bounce. */
export function consumeRouteDenial(): RouteDenial | null {
  const denial = denialRef.value
  denialRef.value = null
  return denial
}

/**
 * Drop the current denial.
 *
 * Called by the guard before a forced logout, by a view on dismiss, and — the
 * call that closes AST3-1 — by the auth store's `clearAuth()`, which is the
 * single teardown behind both `logout()` and the invalid-token branch of
 * session restore. Safe on an already-empty carrier.
 */
export function clearRouteDenial(): void {
  denialRef.value = null
}

/**
 * A new identity now owns this document — drop anything the previous one left.
 *
 * The other half of session scoping, and it is not redundant with
 * `clearRouteDenial()`. `clearAuth()` covers every teardown we control, but an
 * account can be REPLACED without one: a login straight after a restore, or any
 * future account switch that assigns `user` directly. Clearing at the moment an
 * identity is established means the carrier cannot be inherited forwards even
 * when the teardown was skipped. Deliberately distinct from `clearRouteDenial`
 * by name so the call sites read as what they are.
 */
export function beginAuthSession(): void {
  denialRef.value = null
}
